import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { HeaderService, UnidadOperativaItem } from '../../services/header.service';
import { AuthStore, ID_JERARQUIA_TODAS } from '../../../store/auth.store';
import { ActividadGrupal } from '../../services/actividad-grupal.service';
import { NegocioService } from '../../services/negocio.service';
import { VisitasService, ObjetivoVisita } from '../../services/visitas.service';
import { ToastService } from '../../services/toast.service';
import { calcularIdJerarquiaParaCargaUO } from '../../utils/perfil.util';
import {
  extraerAdjuntosDesdeActividad,
  type ActividadGrupalAsistenciaAdjunto,
} from '../../utils/actividad-grupal-asistencia-adjunto.util';
import { abrirODescargarAdjunto } from '../../utils/abrir-adjunto.util';

@Component({
  selector: 'app-detalle-actividad-grupal',
  templateUrl: './detalle-actividad-grupal.component.html',
  styleUrls: ['./detalle-actividad-grupal.component.scss'],
  standalone: false,
})
export class DetalleActividadGrupalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() actividad: ActividadGrupal | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  nombreUnidadOperativa: string | null = null;
  adjuntosDocumentacion: {
    etiqueta: string;
    badgeDetalle: string;
    adjunto: ActividadGrupalAsistenciaAdjunto;
  }[] = [];
  objetivosVisita: ObjetivoVisita[] = [];
  temasNombres: string = '';
  listaTemas: string[] = [];

  private readonly headerService = inject(HeaderService);
  private readonly authStore = inject(AuthStore);
  private readonly negocioService = inject(NegocioService);
  private readonly visitasService = inject(VisitasService);
  private readonly toastService = inject(ToastService);
  private subUO: Subscription | null = null;

  ngOnInit(): void {
    this.visitasService.obtenerObjetivosVisita().subscribe({
      next: (lista) => {
        this.objetivosVisita = Array.isArray(lista) ? lista : [];
        this.actualizarTemasNombres();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['actividad']) {
      this.adjuntosDocumentacion = extraerAdjuntosDesdeActividad(this.actividad).map(
        ({ etiqueta, badgeDetalle, adjunto }) => ({ etiqueta, badgeDetalle, adjunto })
      );
      this.cargarEtiquetaUnidadOperativa();
      this.actualizarTemasNombres();
    }
  }

  ngOnDestroy(): void {
    this.subUO?.unsubscribe();
  }

  cerrar(): void {
    this.visibleChange.emit(false);
  }

  private parsearFechaSegura(fechaISO: string | null | undefined): Date | null {
    if (!fechaISO) return null;
    const d = new Date(fechaISO);
    if (!isNaN(d.getTime())) return d;
    const conT = String(fechaISO).replace(' ', 'T');
    const d2 = new Date(conT);
    if (!isNaN(d2.getTime())) return d2;
    return null;
  }

  formatearFecha(fechaISO: string | null | undefined): string {
    const d = this.parsearFechaSegura(fechaISO);
    if (!d) return String(fechaISO || '—');
    try {
      return d.toLocaleDateString('es-CL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return String(fechaISO);
    }
  }

  formatearHora(fechaISO: string | null | undefined): string {
    const d = this.parsearFechaSegura(fechaISO);
    if (!d) return '';
    try {
      const h = d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
      return h === '00:00' ? '' : h;
    } catch {
      return '';
    }
  }

  obtenerHoraProgramada(det: ActividadGrupal): string {
    const hora = det.detalle?.hora_programada;
    if (!hora) return '';
    try {
      const parts = hora.split(':');
      if (parts.length < 2) return hora;
      let hh = parseInt(parts[0], 10);
      const mm = parts[1];
      const ampm = hh >= 12 ? 'PM' : 'AM';
      hh = hh % 12;
      hh = hh ? hh : 12;
      return `${hh}:${mm} ${ampm}`;
    } catch {
      return hora;
    }
  }

  actualizarTemasNombres(): void {
    const ids = this.actividad?.id_objetivo_visita;
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      this.temasNombres = '—';
      this.listaTemas = [];
      return;
    }
    const nombres = ids
      .map(id => this.objetivosVisita.find(o => o.id_objetivo_visita === id)?.descripcion)
      .filter((desc): desc is string => !!desc);
    this.temasNombres = nombres.length > 0 ? nombres.join(', ') : '—';
    this.listaTemas = nombres;
  }

  getClaseEstado(estado: string | null | undefined): string {
    if (!estado) return '';
    const e = estado.toLowerCase();
    if (e.includes('realiza') || e.includes('completad') || e.includes('finaliz')) return 'estado-finalizada';
    if (e.includes('pendiente') || e.includes('programad') || e.includes('agendad')) return 'estado-agendada';
    if (e.includes('cancel')) return 'estado-cancelada';
    return '';
  }

  textoCategoria(a: ActividadGrupal | null): string {
    if (!a) return '—';
    return (a.categoria_descripcion || a.categoria || '—').trim() || '—';
  }

  abrirDocumento(adjunto: ActividadGrupalAsistenciaAdjunto): void {
    abrirODescargarAdjunto(
      adjunto,
      this.negocioService,
      this.visitasService,
      this.toastService
    );
  }

  textoFechaSubida(adj: ActividadGrupalAsistenciaAdjunto): string {
    const raw = adj.fecha_subida?.trim();
    if (!raw) return '';
    try {
      return new Date(raw).toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return raw;
    }
  }

  private cargarEtiquetaUnidadOperativa(): void {
    this.subUO?.unsubscribe();
    this.subUO = null;
    this.nombreUnidadOperativa = null;

    const a = this.actividad;
    if (a == null || a.id_jerarquia == null) {
      return;
    }

    const idJer = a.id_jerarquia;
    if (idJer === ID_JERARQUIA_TODAS) {
      this.nombreUnidadOperativa = 'Todas las Unidades Operativas';
      return;
    }

    const perfil = this.authStore.obtenerPerfilActivo();
    if (perfil?.id_perfil == null) {
      this.nombreUnidadOperativa = `ID ${idJer}`;
      return;
    }

    const idArea = calcularIdJerarquiaParaCargaUO(
      perfil,
      this.authStore.obtenerIdRegionSeleccionada(),
      this.authStore.obtenerIdAreaSeleccionada()
    );

    this.subUO = this.headerService.obtenerUnidadesOperativas(perfil.id_perfil, idArea).subscribe({
      next: (lista: UnidadOperativaItem[]) => {
        const u = (lista ?? []).find(x => x.id_jerarquia === idJer);
        this.nombreUnidadOperativa = u?.descripcion ?? `Unidad operativa (ID ${idJer})`;
      },
      error: () => {
        this.nombreUnidadOperativa = `Unidad operativa (ID ${idJer})`;
      },
    });
  }

  tieneDescribaOtro(item: any): boolean {
    return 'objetivo_otro' in (item ?? {});
  }

}
